import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Header, Footer } from "@/components/Chrome";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Maisons from "@/pages/Maisons";
import House from "@/pages/House";
import Bourgogne from "@/pages/Bourgogne";
import Sejour from "@/pages/Sejour";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import HomeEn from "@/pages/HomeEn";
import HouseEn from "@/pages/HouseEn";
import ContactEn from "@/pages/ContactEn";
import MaisonsEn from "@/pages/MaisonsEn";
import BourgogneEn from "@/pages/BourgogneEn";
import SejourEn from "@/pages/SejourEn";
import CarnetVoyage from "@/carnet/CarnetVoyage";
import FamilleYvois from "@/famille/FamilleYvois";
import { GITES } from "@/data";

const SITE = "https://lepointdevuedetanlay.fr";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const DEFAULT_TITLE = "Le Point de Vue de Tanlay — Deux gîtes en Bourgogne";
const DEFAULT_DESCRIPTION = "Découvrez Le Point de Vue de Tanlay : deux gîtes indépendants en Bourgogne, Chez Anne et Au 19, avec terrasse et jardin. Contact direct et réservation sur Booking.";

const TITLES = {
  "/": "Le Point de Vue de Tanlay — Deux gîtes en Bourgogne",
  "/les-maisons": "Chez Anne & Au 19 | Le Point de Vue de Tanlay",
  "/chez-anne-tanlay": "Chez Anne — Gîte à Tanlay en Bourgogne | Le Point de Vue",
  "/au-19-tanlay": "Au 19 — Gîte à Tanlay en Bourgogne | Le Point de Vue",
  "/decouvrir-la-bourgogne": "Découvrir Tanlay et la Bourgogne | Le Point de Vue",
  "/carnet-de-voyage-tanlay": "Carnet de voyage autour de Tanlay | Le Point de Vue",
  "/histoire-famille-yvois": "Un verre de chablis chez Anne | Le Point de Vue",
  "/preparer-son-sejour": "Préparer son séjour | Le Point de Vue de Tanlay",
  "/contact": "Contact et réservation | Le Point de Vue de Tanlay",
  "/mentions-legales": "Mentions légales | Le Point de Vue de Tanlay",
  "/politique-de-confidentialite": "Confidentialité | Le Point de Vue de Tanlay",
  "/en": "Holiday Cottages in Tanlay, Burgundy | Le Point de Vue",
  "/en/chez-anne": "Chez Anne: Holiday Cottage in Tanlay, Sleeps 5",
  "/en/au-19": "Au 19: Holiday Cottage in Tanlay, Sleeps 5",
  "/en/contact": "Contact & Booking | Le Point de Vue de Tanlay",
  "/en/cottages": "Our Holiday Cottages in Tanlay | Le Point de Vue",
  "/en/explore-burgundy": "Explore Tanlay & Burgundy | Le Point de Vue",
  "/en/tanlay-travel-journal": "Travel journal around Tanlay | Le Point de Vue",
  "/en/yvois-family-story": "A Glass of Chablis with Anne | Le Point de Vue",
  "/en/plan-your-stay": "Plan Your Stay in Tanlay | Le Point de Vue",
  "/en/legal-notice": "Legal Notice | Le Point de Vue de Tanlay",
  "/en/privacy-policy": "Privacy Policy | Le Point de Vue de Tanlay",
};

const DESCRIPTIONS = {
  "/": DEFAULT_DESCRIPTION,
  "/les-maisons": "Comparez Chez Anne et Au 19, deux gîtes indépendants à Tanlay en Bourgogne. Découvrez leurs espaces, leurs équipements et leurs photos pour choisir votre séjour.",
  "/chez-anne-tanlay": "Découvrez Chez Anne, un gîte de 150 m² à Tanlay pour 5 voyageurs, avec deux chambres, cheminée, grande terrasse et jardin. Contact direct ou réservation sur Booking.",
  "/au-19-tanlay": "Découvrez Au 19, un gîte de 120 m² à Tanlay pour 5 voyageurs, avec deux chambres, cheminée, terrasse et jardin. Contact direct ou réservation sur Booking.",
  "/decouvrir-la-bourgogne": "Préparez vos découvertes autour de Tanlay : châteaux, canal de Bourgogne, promenades et vignobles de Chablis, avec des liens vers les sites des lieux et du tourisme.",
  "/carnet-de-voyage-tanlay": "Explorez Tanlay, Noyers et le Tonnerrois à travers cartes postales anciennes, récits historiques, châteaux, canal de Bourgogne et portraits de femmes.",
  "/histoire-famille-yvois": "Un portrait d'Anne Yvois raconté par Ben : les gîtes bâtis par son père Georges, les recherches de sa mère Marguerite, et le destin d'Henriette Virginie Yvois.",
  "/preparer-son-sejour": "Retrouvez les informations pratiques pour votre séjour au Point de Vue de Tanlay : arrivée, départ, stationnement, Wi-Fi, animaux et contact avec Anne.",
  "/contact": "Contactez Anne pour préparer votre séjour au Point de Vue de Tanlay. Retrouvez ses coordonnées et les liens Booking des gîtes Chez Anne et Au 19.",
  "/mentions-legales": "Consultez les mentions légales du site Le Point de Vue de Tanlay, qui présente les gîtes Chez Anne et Au 19 et leurs possibilités de réservation.",
  "/politique-de-confidentialite": "Consultez les informations sur les données personnelles, les prises de contact et les traitements techniques du site Le Point de Vue de Tanlay.",
  "/en": "Discover Le Point de Vue de Tanlay: two independent holiday cottages in Burgundy, Chez Anne and Au 19, with terrace and garden. Contact Anne directly or book on Booking.",
  "/en/chez-anne": "Chez Anne is a 150 m² holiday cottage in Tanlay, Burgundy, that sleeps up to five, with two bedrooms, a fireplace, a large terrace and a garden. Book on Booking or contact Anne.",
  "/en/au-19": "Au 19 is a 120 m² holiday cottage in Tanlay, Burgundy, that sleeps up to five, with two bedrooms, a fireplace, a terrace and a garden. Book on Booking or contact Anne.",
  "/en/contact": "Contact Anne to plan your stay at Le Point de Vue de Tanlay. Find her phone and email and the Booking links for the Chez Anne and Au 19 cottages.",
  "/en/cottages": "Compare Chez Anne and Au 19, two independent holiday cottages in Tanlay, Burgundy. Discover their spaces and features, and book one or both cottages for up to ten guests.",
  "/en/explore-burgundy": "Plan your discoveries around Tanlay: Renaissance châteaux, the Burgundy Canal, walks and the Chablis vineyards, with links to the official sites of each place.",
  "/en/tanlay-travel-journal": "Explore Tanlay, Noyers and nearby Burgundy through historic postcards, local stories, châteaux, the Burgundy Canal and the women who shaped its history.",
  "/en/yvois-family-story": "A portrait of Anne Yvois told by Ben: the cottages built by her father Georges, her mother Marguerite's research, and the fate of Henriette Virginie Yvois.",
  "/en/plan-your-stay": "Practical information for your stay at Le Point de Vue de Tanlay: arrival, departure, parking, Wi-Fi, pets and how to contact Anne.",
  "/en/legal-notice": "Legal notice for the Le Point de Vue de Tanlay website, presenting the Chez Anne and Au 19 holiday cottages and their booking options.",
  "/en/privacy-policy": "Information about personal data, contact methods and technical processing on the Le Point de Vue de Tanlay website.",
};

const OG_IMAGES = {
  "/chez-anne-tanlay": GITES[0].cover,
  "/au-19-tanlay": GITES[1].cover,
  "/en/chez-anne": GITES[0].cover,
  "/en/au-19": GITES[1].cover,
};
const DEFAULT_OG_IMAGE = GITES[0].cover;

// Paires FR <-> EN pour hreflang et URL canoniques
const PAIR = {
  "/": "/en", "/en": "/",
  "/chez-anne-tanlay": "/en/chez-anne", "/en/chez-anne": "/chez-anne-tanlay",
  "/au-19-tanlay": "/en/au-19", "/en/au-19": "/au-19-tanlay",
  "/contact": "/en/contact", "/en/contact": "/contact",
  "/les-maisons": "/en/cottages", "/en/cottages": "/les-maisons",
  "/decouvrir-la-bourgogne": "/en/explore-burgundy", "/en/explore-burgundy": "/decouvrir-la-bourgogne",
  "/carnet-de-voyage-tanlay": "/en/tanlay-travel-journal", "/en/tanlay-travel-journal": "/carnet-de-voyage-tanlay",
  "/histoire-famille-yvois": "/en/yvois-family-story", "/en/yvois-family-story": "/histoire-famille-yvois",
  "/preparer-son-sejour": "/en/plan-your-stay", "/en/plan-your-stay": "/preparer-son-sejour",
  "/mentions-legales": "/en/legal-notice", "/en/legal-notice": "/mentions-legales",
  "/politique-de-confidentialite": "/en/privacy-policy", "/en/privacy-policy": "/politique-de-confidentialite",
};
const GITE_BY_PATH = {
  "/chez-anne-tanlay": GITES[0], "/en/chez-anne": GITES[0],
  "/au-19-tanlay": GITES[1], "/en/au-19": GITES[1],
};
const FR_CANONICAL_OF_GITE = { "chez-anne": SITE + "/chez-anne-tanlay", "au-19": SITE + "/au-19-tanlay" };

const isEnPath = (p) => p === "/en" || p.startsWith("/en/");
const canonicalFor = (p) => (p === "/" ? SITE + "/" : p === "/en" ? SITE + "/en/" : SITE + p);

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function buildGiteJsonLd(gite, description) {
  const id = FR_CANONICAL_OF_GITE[gite.id];
  return {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    "@id": `${id}#gite`,
    name: gite.name,
    url: id,
    description,
    image: gite.cover,
    numberOfBedrooms: gite.bedrooms,
    petsAllowed: true,
    occupancy: { "@type": "QuantitativeValue", maxValue: gite.guests },
    floorSize: { "@type": "QuantitativeValue", unitCode: "MTK", value: gite.sqm },
    amenityFeature: gite.features.map((f) => ({ "@type": "LocationFeatureSpecification", name: f, value: true })),
    containedInPlace: {
      "@type": "Place",
      address: { "@type": "PostalAddress", addressLocality: "Tanlay", postalCode: "89430", addressRegion: "Bourgogne-Franche-Comté", addressCountry: "FR" },
    },
  };
}

function setHreflang(pathname) {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  const counterpart = PAIR[pathname];
  if (!counterpart) return; // pas de traduction déclarée pour les pages sans équivalent
  const isEn = isEnPath(pathname);
  const frPath = isEn ? counterpart : pathname;
  const enPath = isEn ? pathname : counterpart;
  [["fr", canonicalFor(frPath)], ["en", canonicalFor(enPath)]].forEach(([lang, href]) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", lang);
    el.setAttribute("href", href);
    document.head.appendChild(el);
  });
}

function DocumentTitle() {
  const { pathname: raw } = useLocation();
  useEffect(() => {
    const pathname = raw !== "/" ? raw.replace(/\/+$/, "") : raw;
    const isEn = isEnPath(pathname);
    const title = TITLES[pathname] || DEFAULT_TITLE;
    const description = DESCRIPTIONS[pathname] || DEFAULT_DESCRIPTION;
    const canonicalUrl = canonicalFor(pathname);
    const image = OG_IMAGES[pathname] || DEFAULT_OG_IMAGE;

    document.documentElement.lang = isEn ? "en" : "fr";
    document.title = title;
    upsertMeta("name", "description", description);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "Le Point de Vue de Tanlay");
    upsertMeta("property", "og:locale", isEn ? "en_GB" : "fr_FR");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", image);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    setHreflang(pathname);

    // JSON-LD sur les pages de gîte (FR et EN) — même entité, identifiant stable
    let script = document.getElementById("route-jsonld");
    const gite = GITE_BY_PATH[pathname];
    if (gite) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "route-jsonld";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(buildGiteJsonLd(gite, description));
    } else if (script) {
      script.remove();
    }
  }, [raw]);
  return null;
}

export default function App() {
  return (
    <div className="App min-h-screen flex flex-col bg-cream">
      <BrowserRouter>
        <ScrollToTop />
        <DocumentTitle />
        <Toaster position="top-center" richColors />
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/les-maisons" element={<Maisons />} />
            <Route path="/chez-anne-tanlay" element={<House id="chez-anne" />} />
            <Route path="/au-19-tanlay" element={<House id="au-19" />} />
            <Route path="/decouvrir-la-bourgogne" element={<Bourgogne />} />
            <Route path="/carnet-de-voyage-tanlay" element={<CarnetVoyage locale="fr" />} />
            <Route path="/histoire-famille-yvois" element={<FamilleYvois locale="fr" />} />
            <Route path="/preparer-son-sejour" element={<Sejour />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/mentions-legales" element={<Legal type="legal" />} />
            <Route path="/politique-de-confidentialite" element={<Legal type="privacy" />} />
            <Route path="/en" element={<HomeEn />} />
            <Route path="/en/chez-anne" element={<HouseEn id="chez-anne" />} />
            <Route path="/en/au-19" element={<HouseEn id="au-19" />} />
            <Route path="/en/contact" element={<ContactEn />} />
            <Route path="/en/cottages" element={<MaisonsEn />} />
            <Route path="/en/explore-burgundy" element={<BourgogneEn />} />
            <Route path="/en/tanlay-travel-journal" element={<CarnetVoyage locale="en" />} />
            <Route path="/en/yvois-family-story" element={<FamilleYvois locale="en" />} />
            <Route path="/en/plan-your-stay" element={<SejourEn />} />
            <Route path="/en/legal-notice" element={<Legal type="legal" lang="en" />} />
            <Route path="/en/privacy-policy" element={<Legal type="privacy" lang="en" />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}
